import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';

import { buildRequestsRegisterToolbarConfig } from './registerToolbarConfig';

interface Props {
  title: string;
  /** Already-formatted count display string (e.g. `getCounter(...)` → `(12)`). */
  counter?: string;
}

/**
 * Object toolbar for the Requests (change-request) register, mounted only when
 * the `dynamic_forms` flag is on. The Requests register has no export button
 * and no create action, so this toolbar surfaces only the relocated ribbon
 * "Edit Filters" opener (bridged up via the toolbar form store when the user can
 * manage the ribbon) plus the auto-injected Toolbar settings action (CUS-136).
 */
export const RequestsRegisterToolbar: FC<Props> = ({ title, counter }) => {
  const { t } = useTranslation(['common']);
  // Ribbon "Edit Filters" opener bridged up from CustomisableRibbon (relocate
  // mode). Present only when the user can manage the ribbon; drives the toolbar's
  // overflow "Edit" group.
  const ribbonConfig = useObjectToolbarFormStore((s) => s.ribbonConfigState);

  const toolbarConfig = useMemo(
    () =>
      buildRequestsRegisterToolbarConfig({
        title,
        counter,
        // Requests has no export path; the shared args type still requires this.
        exportOptions: [],
        editRibbon: ribbonConfig
          ? {
              groupLabel: t('edit'),
              label: t('objectToolbar.filterSettings'),
              onClick: ribbonConfig.openEditRibbon,
            }
          : undefined,
      }),
    [title, counter, ribbonConfig, t]
  );

  return <ObjectToolbar config={toolbarConfig} />;
};
